// Password helpers shared by PasswordFields and the auth pages.

export const PASSWORD_MIN = 8;

// Ambiguous glyphs (l/1/I/O/0) left out so a generated password survives being
// read off a screen or a sticky note.
const LOWER = "abcdefghijkmnopqrstuvwxyz";
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const DIGITS = "23456789";
const SYMBOLS = "!@#$%&*?";

/** Every character a generated password can contain (used by the scramble animation). */
export const PASSWORD_ALPHABET = LOWER + UPPER + DIGITS + SYMBOLS;

/** 16 chars, at least one of each class, crypto-random, shuffled. */
export function generatePassword(length = 16): string {
  const pick = (set: string, n: number) =>
    Array.from(crypto.getRandomValues(new Uint32Array(n)), (v) => set[v % set.length]);
  const chars = [
    ...pick(LOWER, 1),
    ...pick(UPPER, 1),
    ...pick(DIGITS, 1),
    ...pick(SYMBOLS, 1),
    ...pick(PASSWORD_ALPHABET, length - 4),
  ];
  const rnd = crypto.getRandomValues(new Uint32Array(chars.length));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rnd[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

export type PasswordScore = 0 | 1 | 2 | 3 | 4;

/** 0 = too short, 1 weak … 4 strong. Cheap heuristic, not a policy. */
export function passwordScore(pw: string): PasswordScore {
  if (pw.length < PASSWORD_MIN) return 0;
  let s = 1;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  return s as PasswordScore;
}

/** True when the pair is ready to submit. Pages use it to gate the button. */
export function passwordsReady(password: string, confirm: string): boolean {
  return password.length >= PASSWORD_MIN && confirm === password;
}
